import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router'
import { Toaster } from 'sonner'
import { isBackendStarting } from '@/lib/api-client'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ThemeProvider, useTheme } from '@/components/providers/theme-provider'
import { AuthProvider, useAuth } from '@/components/providers/auth-provider'
import { LocaleProvider, useLocale } from '@/components/providers/locale-provider'
import { guardUnload } from '@/lib/ingest-store'
import { LOCALES } from '@/lib/i18n'
import { ROUTE_IDS, ROUTE_SEGMENTS, matchRoute, pathFor, type RouteId } from '@/lib/i18n/routes'
import { LoginPage } from '@/pages/login'
import { Loader2 } from 'lucide-react'
import { AppShell } from '@/components/app-shell'
import { TermBasePage } from '@/pages/term-base'
import { PreviewPage } from '@/pages/preview'
import { SettingsPage } from '@/pages/settings'
import { JobsPage } from '@/pages/jobs'
import { PromptsPage } from '@/pages/prompts'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      // Keep retrying while the backend is still loading its models, so the UI
      // recovers on its own after a restart instead of stranding the user.
      retry: (failureCount, error) =>
        isBackendStarting(error) ? failureCount < 10 : failureCount < 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
})

/** Sonner needs the resolved theme explicitly to match the app. */
function ThemedToaster() {
  const { resolved } = useTheme()
  return <Toaster theme={resolved} position="bottom-right" richColors closeButton />
}

/** Nothing below this renders until we know who the user is. */
function Authenticated({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  return user ? <>{children}</> : <LoginPage />
}

const PAGE: Record<RouteId, React.ReactElement> = {
  termBase: <TermBasePage />,
  preview: <PreviewPage />,
  jobs: <JobsPage />,
  prompts: <PromptsPage />,
  settings: <SettingsPage />,
}

/**
 * Rewrite the address bar into the reader's own language.
 *
 * Every locale's segments are registered below, so an Estonian link opens fine
 * for someone reading in Finnish — this is what then swaps `/terminibaas` for
 * `/termipankki` so the URL matches the page they are actually looking at.
 * Search and hash are preserved: the query params carry the whole view state.
 */
function LocaleUrlSync() {
  const { locale } = useLocale()
  const location = useLocation()
  const navigate = useNavigate()

  // A reload discards files that have not been transferred yet — the bytes
  // live in this page. Everything already sent keeps processing regardless, so
  // this only guards the part that genuinely cannot be recovered.
  useEffect(() => guardUnload(), [])

  useEffect(() => {
    const match = matchRoute(location.pathname)
    if (!match || match.locale === locale) return
    navigate(
      {
        pathname: pathFor(match.id, locale, match.rest),
        search: location.search,
        hash: location.hash,
      },
      { replace: true },
    )
  }, [locale, location.pathname, location.search, location.hash, navigate])

  return null
}

/** Old /proovi/<id> links keep working after the rename. */
function LegacyPreviewRedirect() {
  const { sessionId } = useParams<{ sessionId?: string }>()
  const { locale } = useLocale()
  return <Navigate to={pathFor('preview', locale, sessionId ?? '')} replace />
}

/** Anything unrecognised lands on the term base, in the reader's language. */
function HomeRedirect() {
  const { locale } = useLocale()
  return <Navigate to={pathFor('termBase', locale)} replace />
}

function AppRoutes() {
  const { locale } = useLocale()
  return (
    // Remounting on a language change is the cheapest way to guarantee that
    // number and date formatting — which reads the locale outside React — is
    // never left showing the previous language's separators.
    <Routes key={locale}>
      <Route path="/" element={<HomeRedirect />} />

      {/* Every locale's spelling of every page resolves, so shared links do not
          break across languages; LocaleUrlSync then normalises the address. */}
      {LOCALES.flatMap((loc) =>
        ROUTE_IDS.map((id) => (
          <Route key={`${loc}:${id}`} path={`/${ROUTE_SEGMENTS[loc][id]}`} element={PAGE[id]} />
        )),
      )}
      {LOCALES.map((loc) => (
        <Route
          key={`${loc}:preview-session`}
          path={`/${ROUTE_SEGMENTS[loc].preview}/:sessionId`}
          element={<PreviewPage />}
        />
      ))}

      {/* Links shared before the /proovi -> /eelvaade rename still resolve.
          Both forms land on the preview page; without an id that is its list. */}
      <Route path="/proovi" element={<LegacyPreviewRedirect />} />
      <Route path="/proovi/:sessionId" element={<LegacyPreviewRedirect />} />

      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LocaleProvider>
          <AuthProvider>
            <TooltipProvider delayDuration={400}>
              <BrowserRouter>
                <LocaleUrlSync />
                <Authenticated>
                  <AppShell>
                    <AppRoutes />
                  </AppShell>
                </Authenticated>
                <ThemedToaster />
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
